import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ReportImpactSummaryHeader from './ReportImpactSummaryHeader';
import ReportImpactSummarySection from './ReportImpactSummarySection';
import { ReportAnswers } from '../../FeedbackReport';
import {
  IMPACT_SUMMARY_QUESTION_ID,
  INDICATOR_DETAILS_QUESTION_ID,
} from 'npo/pages/NPOFeedback/utils';
import { parseImpactSummaryAnswer } from 'npo/pages/NPOFeedback/FeedbackDetails/ImpactSummaryDetails';
import { parseImpactDetailsAnswer } from '../../FeedbackImpactDetails';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { orderedIndicatorSectionList } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import { DATES_QUESTION_ID_1 } from 'npo/pages/ReportBuilder/Components/ReportFinalize/answersUtil';
import { getReachIndicator } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/utils';
import TrueImpactDebug from 'npo/pages/ReportBuilder/TrueImpactDebug';
import { useIndicatorsById } from 'shared/apollo/pageHooks/useAllIndicators';
import { parseReportPeriod } from 'npo/pages/ReportBuilder/Components/SectionPeriod/utils';
import {
  getServiceIndicators,
  useAllServiceCollections,
} from 'shared/apollo/pageHooks/useAllServiceCollections';
import { useIndicator } from 'shared/providers/IndicatorProvider';

declare global {
  interface Window {
    getAnswers?: () => object;
  }
}

interface ReportImpactSummaryTableProps {
  reportId: number | string;
  programName?: string;
  answers: ReportAnswers;
  isLegacy: boolean;
}

const StyledDiv = styled.div`
  background-color: ${COLORS.whiteBase};
`;

const ReportImpactSummaryTable: React.FC<ReportImpactSummaryTableProps> = ({
  answers,
  reportId,
  programName,
  isLegacy,
}) => {
  const { data: indicators } = useIndicatorsById();
  const { data: collections } = useAllServiceCollections({
    reportId,
  });
  const {
    isModalEmpty,
    impactSummary: initialImpactSummary,
    impactDetails: initialImpactDetails,
  } = useIndicator();
  const [isDebug, setIsDebug] = useState<boolean>(false);
  const {
    impactSummary: fallBackImpactSummary,
    impactDetails: fallbackImpactDetails,
    beneficiaryTerm,
    intermediaries,
    reportPeriod,
    serviceGoal,
    serviceType,
    isReportPassed,
    serviceMainGoal,
    serviceIndicators,
    reachHumanIndicator,
    learnIndicator,
    actIndicator,
  } = useMemo(() => {
    const impactSummary = parseImpactSummaryAnswer(
      answers.impact_summary?.find(
        x => x.question.dbId === IMPACT_SUMMARY_QUESTION_ID,
      )?.text,
    );
    const impactDetails = parseImpactDetailsAnswer(
      answers.indicator_details?.find(
        x => x.question.dbId === INDICATOR_DETAILS_QUESTION_ID,
      )?.text,
    );
    const beneficiaryTerm = answers.impact_summary?.find(
      x => x.question.dbId === 40004,
    )?.text;
    const intermediaries = answers.impact_summary?.find(
      x => x.question.dbId === 40022,
    )?.text;
    const reportPeriod = answers.dates?.find(
      x => x.question.dbId === DATES_QUESTION_ID_1,
    )?.text;
    const serviceMainGoal = answers.impact_summary?.find(
      x => x.question.dbId === 40002,
    )?.text;
    const serviceGoal = answers.impact_summary?.find(
      x => x.question.dbId === 40003,
    )?.text;
    const serviceType = answers.impact_summary?.find(
      x => x.question.dbId === 40006,
    )?.text;
    const reachHumanIndicator = impactDetails
      ? getReachIndicator(impactDetails.reach)
      : undefined;
    const learnIndicator = impactDetails?.learn[0];
    const actIndicator = impactDetails?.act[0];
    return {
      impactSummary,
      impactDetails,
      beneficiaryTerm,
      intermediaries,
      serviceGoal,
      serviceType,
      reachHumanIndicator,
      learnIndicator,
      actIndicator,
      serviceMainGoal,
      serviceIndicators: getServiceIndicators(
        serviceMainGoal,
        serviceGoal,
        collections,
      ),
      isReportPassed: true,
      reportPeriod: parseReportPeriod(reportPeriod),
    };
  }, [answers, collections]);

  const isEmpty = isModalEmpty();
  const impactSummary = isEmpty ? fallBackImpactSummary : initialImpactSummary;
  const impactDetails = isEmpty ? fallbackImpactDetails : initialImpactDetails;

  const debug = useMemo(() => {
    return {
      answers,
      reportId,
      impactSummary,
      impactDetails,
      beneficiaryTerm,
      intermediaries,
      reportPeriod,
      serviceGoal,
      serviceType,
      isReportPassed,
      serviceMainGoal,
      serviceIndicators,
      reachHumanIndicator,
      learnIndicator,
      actIndicator,
      programName,
    };
  }, [answers, collections]);

  useEffect(() => {
    if (isDebug) {
      window.getAnswers = () => debug;
    }
  }, [isDebug]);

  const handleDebug = useCallback(() => setIsDebug(o => !o), [setIsDebug]);

  if (!impactSummary || !impactDetails) return null;

  return (
    <StyledDiv>
      <TrueImpactDebug
        isDebug={isDebug}
        onClick={() => console.log(debug)}
        handleChange={handleDebug}
      />
      <ReportImpactSummaryHeader />
      {orderedIndicatorSectionList.map(x => (
        <ReportImpactSummarySection
          key={x}
          sectionKey={x}
          reportId={reportId}
          impactSummary={impactSummary}
          impactDetails={impactDetails}
          beneficiaryTerm={beneficiaryTerm}
          intermediaries={intermediaries}
          reportPeriod={reportPeriod}
          serviceGoal={serviceGoal}
          serviceType={serviceType}
          isReportPassed={isReportPassed}
          reachHumanIndicator={reachHumanIndicator}
          learnIndicator={learnIndicator}
          actIndicator={actIndicator}
          programName={programName}
          indicators={indicators}
          isLegacy={isLegacy}
        />
      ))}
    </StyledDiv>
  );
};

export default ReportImpactSummaryTable;
